'use client';

import { Check } from 'lucide-react';
import { RadioGroup as RadixRadioGroup } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';
import { cn, focusRing } from './cn';
import { segmentClass, segmentTrackClass } from './segmented-control';

/** The round mark of a choice, checked in the accent. Pass `checked` where no Radix state drives it. */
function radioDotClass(checked?: boolean) {
  return cn(
    'flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors duration-[var(--crow-dur-1)]',
    checked === undefined
      ? 'border-crow-input bg-crow-surface group-data-[state=checked]/radio:border-crow-accent group-data-[state=checked]/radio:bg-crow-accent'
      : checked
        ? 'border-crow-accent bg-crow-accent'
        : 'border-crow-input bg-crow-surface',
  );
}

/**
 * A group of choices, one picked: arrows move between them and pick, as radios do. `look`
 * `"rows"` stacks RadioGroupItem rows; `"segments"` draws the group as a segmented control's
 * track, for a few short choices with `look="segment"` items (a SegmentedControl that is a real
 * radio group: arrows pick, a disabled choice is skipped).
 */
export function RadioGroup({
  className,
  look = 'rows',
  ...props
}: ComponentProps<typeof RadixRadioGroup.Root> & { look?: 'rows' | 'segments' }) {
  return (
    <RadixRadioGroup.Root
      className={cn(look === 'segments' ? segmentTrackClass : 'grid gap-2', className)}
      {...props}
    />
  );
}

/**
 * One choice of a RadioGroup: a whole row with the round mark (unless `indicator` is false)
 * beside its content, or, with `look="segment"`, a segment of a `look="segments"` group.
 */
export function RadioGroupItem({
  className,
  children,
  indicator = true,
  look = 'row',
  ...props
}: ComponentProps<typeof RadixRadioGroup.Item> & {
  indicator?: boolean;
  look?: 'row' | 'segment';
}) {
  if (look === 'segment')
    return (
      <RadixRadioGroup.Item className={cn(segmentClass('checked'), className)} {...props}>
        {children}
      </RadixRadioGroup.Item>
    );
  return (
    <RadixRadioGroup.Item
      className={cn(
        'group/radio flex w-full items-start gap-3 rounded-crow-panel border border-crow-line px-3.5 py-2.5 text-left text-crow-body text-crow-text transition-colors duration-[var(--crow-dur-1)] hover:bg-crow-raised disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-crow-accent data-[state=checked]:bg-crow-accent-tint',
        focusRing,
        className,
      )}
      {...props}
    >
      {indicator ? (
        <span aria-hidden className={cn(radioDotClass(), 'mt-0.5')}>
          <RadixRadioGroup.Indicator className="size-1.5 rounded-full bg-crow-on-accent" />
        </span>
      ) : null}
      {children}
    </RadixRadioGroup.Item>
  );
}

export type RadioCardOption<V extends string> = {
  value: V;
  label: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  /** Spans the whole row (a first « follow the default » choice above the others). */
  wide?: boolean;
  /** A tooltip, e.g. why the choice is disabled. */
  title?: string;
};

/** Mutually exclusive choices shown as cards, the chosen one checked in its corner. */
export function RadioCards<V extends string>({
  value,
  onValueChange,
  options,
  disabled,
  columns = 3,
  name,
  className,
  'aria-label': ariaLabel,
}: {
  value: V;
  onValueChange: (value: V) => void;
  options: readonly RadioCardOption<V>[];
  disabled?: boolean;
  columns?: 2 | 3;
  /** Submits the value with a surrounding form under this name. */
  name?: string;
  className?: string;
  'aria-label'?: string;
}) {
  return (
    <RadixRadioGroup.Root
      value={value}
      onValueChange={(next) => onValueChange(next as V)}
      disabled={disabled}
      name={name}
      aria-label={ariaLabel}
      className={cn('grid gap-2', columns === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-3', className)}
    >
      {options.map((option) => (
        <RadixRadioGroup.Item
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          title={option.title}
          className={cn(
            option.wide && 'col-span-full',
            'group/radio relative flex flex-col items-start gap-1 rounded-crow-panel border border-crow-line p-3 pr-8 text-left text-crow-body text-crow-text transition-colors duration-[var(--crow-dur-1)] hover:border-crow-line-strong hover:bg-crow-raised disabled:opacity-50 data-[state=checked]:border-crow-accent data-[state=checked]:bg-crow-accent-tint data-[state=checked]:hover:bg-crow-accent-tint',
            focusRing,
          )}
        >
          <span className="flex items-center gap-2 font-semibold">
            {option.icon ? (
              <span aria-hidden className="text-crow-text-2 [&>svg]:size-4">
                {option.icon}
              </span>
            ) : null}
            {option.label}
          </span>
          {option.description ? (
            <span className="text-crow-label text-crow-text-2">{option.description}</span>
          ) : null}
          <RadixRadioGroup.Indicator className="absolute top-3 right-3">
            <Check aria-hidden className="size-3.5 text-crow-accent-ink" />
          </RadixRadioGroup.Indicator>
        </RadixRadioGroup.Item>
      ))}
    </RadixRadioGroup.Root>
  );
}

/**
 * A radio dot over a native radio input, for a choice laid out by its caller: it goes inside the
 * caller's own `<label>` (an option card, say), so the whole label stays clickable.
 */
export function Radio({
  name,
  value,
  checked,
  onChange,
  disabled = false,
  id,
  className,
  'aria-label': ariaLabel,
}: {
  /** Id of the input, for a `<label htmlFor>` that does not wrap it. */
  id?: string;
  name: string;
  value?: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
}) {
  return (
    <span className={cn('relative inline-flex size-4 shrink-0', className)}>
      <input
        id={id}
        type="radio"
        name={name}
        value={value}
        aria-label={ariaLabel}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="peer absolute inset-0 m-0 cursor-[inherit] opacity-0"
      />
      <span
        aria-hidden
        className={cn(
          radioDotClass(checked),
          'pointer-events-none peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-crow-focus-ring peer-focus-visible:outline-solid peer-disabled:opacity-50',
          !checked && 'peer-hover:border-crow-line-strong',
        )}
      >
        {checked ? <span className="size-1.5 rounded-full bg-crow-on-accent" /> : null}
      </span>
    </span>
  );
}
